import type {ReactElement} from 'react';

import {css} from '@emotion/react';
import {motion} from 'motion/react';
import {Fragment, useEffect, useRef} from 'react';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useLocation} from 'react-router-dom';

import Close from '../assets/icons/Close';
import useViewer from '../hooks/useViewer';
import {linkToApp} from '../lib/url/linkToApp';
import {useColor, useComponent} from '../styles';
import useAppTheme from '../styles/useAppTheme';
import LanguageMenu from './LanguageMenu';
import ProfileImg from './ProfileImg';

export type BaseMobileMenuDrawerProps = {
  nav: ReactElement;
  onCloseClick: () => void;
};

/**
 * BaseMobileMenuDrawer displays a mobile navigation drawer with language selection, viewer info, and navigation links.
 *
 * @component
 * @param {BaseMobileMenuDrawerProps} props - Props for the BaseMobileMenuDrawer component.
 * @returns {JSX.Element} The rendered mobile menu drawer.
 */
const BaseMobileMenuDrawer = (
  props: BaseMobileMenuDrawerProps,
): JSX.Element => {
  const {onCloseClick, nav} = props;
  const {spacings, shadows, font, colors} = useAppTheme();
  const {palette} = useColor();
  const {buttons} = useComponent();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const location = useLocation();
  const lastLocation = useRef<string>();
  const isFirstRender = useRef(true);

  // Close on location change
  // Handles external navigation (e.g., browser back/forward)
  useEffect(() => {
    if (!isFirstRender.current && lastLocation.current !== location.pathname)
      onCloseClick();
    else {
      lastLocation.current = location.pathname;
      isFirstRender.current = false;
    }
  }, [location, onCloseClick]);

  const dialogContent = css({
    width: spacings.px375,
    height: '100%',
    background: palette.background.white.defaultColor,
    alignSelf: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: shadows.m,
    overflowY: 'hidden',
  });

  const drawerHeaderCss = css({
    display: 'flex',
    padding: spacings.px20,
  });

  const drawerContentCss = css({
    padding: `0 ${spacings.px20} ${spacings.px20} ${spacings.px20}`,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: spacings.px32,
  });

  const closeButtonCss = css({
    fontSize: spacings.px24,
    marginLeft: 'auto',
  });

  const languageMenuContainer = css({
    display: 'flex',
    justifyContent: 'center',
  });

  const loginButtonCss = css([buttons.secondarySubdued.l, {width: '100%'}]);
  const joinButtonCss = css([buttons.accent.l, {width: '100%'}]);

  const renderGuestButtons = () => {
    if (viewer.id) return;

    return (
      <Fragment>
        <Link
          css={loginButtonCss}
          onClick={onCloseClick}
          to={linkToApp('/login')}
        >
          Log in
        </Link>
        <Link
          css={joinButtonCss}
          onClick={onCloseClick}
          to={linkToApp('/signup')}
        >
          Join free today
        </Link>
      </Fragment>
    );
  };

  const viewerSectionCss = css({
    display: 'flex',
    gap: spacings.px16,
    alignItems: 'center',
  });

  const profileImgContainerCss = css({
    minWidth: spacings.px48,
    minHeight: spacings.px48,
    width: spacings.px48,
    height: spacings.px48,
    overflow: 'hidden',
    borderRadius: spacings.px48,
  });

  const viewerNameCss = css({
    fontWeight: font.weight.bold,

    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  });

  const viewerNameContainerCss = css({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: spacings.px215,
  });

  const viewerActionTextCss = css({
    fontSize: spacings.px10,
    color: colors['brown-600'],
  });

  const renderViewer = () => {
    const {id, profile} = viewer;
    if (!id) return;

    const renderViewerName = () => {
      if (!profile) return;
      const {firstName, lastName} = profile;
      return <span css={viewerNameCss}>{`${firstName} ${lastName}`}</span>;
    };

    return (
      <Link
        css={viewerSectionCss}
        onClick={onCloseClick}
        to={linkToApp('/')}
      >
        <div css={profileImgContainerCss}>
          <ProfileImg src={profile?.imageUrl} />
        </div>
        <div css={viewerNameContainerCss}>
          {renderViewerName()}
          <span css={viewerActionTextCss}>
            {t('nav.side-nav-home-description')}
          </span>
        </div>
      </Link>
    );
  };

  return (
    <motion.section
      animate={{x: '0'}} // It needs to be a string to trigger the initial animation
      css={dialogContent}
      exit={{x: '100%'}}
      initial={{x: 375}}
      transition={{bounce: 0}}
    >
      <header css={drawerHeaderCss}>
        <button
          css={closeButtonCss}
          onClick={onCloseClick}
        >
          <Close />
        </button>
      </header>
      <div css={drawerContentCss}>
        <div css={languageMenuContainer}>
          <LanguageMenu />
        </div>
        {renderViewer()}
        {nav}
        {renderGuestButtons()}
      </div>
    </motion.section>
  );
};

export default BaseMobileMenuDrawer;
